import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { anomalyReasonText } from '../../../utils/anomalySignals';
import IronSightMark from '../../shared/IronSightMark';
import { formatCurrency, formatDate, formatNumber, getBuyerHeatStyle } from '../../../utils/formatting';
import { aiPriceGapDisplay } from '../../../utils/aiPrice';
import { useDealershipSettings } from '../../../contexts/settingsContext';
import { getListingAgeColor, getCompletenessColor, getRiskTierStyle } from '../../../utils/unitDetailHelpers';
import CarryingCostSection from './CarryingCostSection';
import { peerSummaries } from '../../../utils/peerSummaries';
import {
    advertisedVsInternal, anomalyContext, buildTrajectory, hasAnyInsight as computeHasAnyInsight, hasQuoteInterest as computeHasQuoteInterest,
    hasSellSpeedData as computeHasSellSpeedData, peerComparison, PEER_BAND_COLORS, riskRecommendation, saleCountdown, survivalPoints,
} from '../../../utils/unitIntelligence';
import SOURCE_LABELS from '../../../config/sourceLabels';
import './IronSightCards.css';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

function AITab({ unit, mfData, mfLoading, calcPrice, setCalcPrice, onSwitchTab, comps, pipeline, quoteEquipment, predictions12m, predictions12mLoading }) {
    const { gapThresholds } = useDealershipSettings();
    const heatStatus = getBuyerHeatStyle(unit.QuoteCount, unit.DaysSinceLastQuote);
    const hasSellSpeedData = computeHasSellSpeedData(unit);
    const riskStyle = getRiskTierStyle(unit.RiskTier);

    // Peer summaries from backend comps response (single source of truth)
    // Peer averages without the unit itself (utils/marketComps peerSummaries).
    const peerStats = peerSummaries(comps, unit.StockNumber);
    const mfListing = peerStats.listing_duration_summary;
    const hoursStats = peerStats.hours_summary;

    const aiPrice = unit.PredictedPrice || null;
    const askingPrice = unit.Best_Sell_Price;
    const hasPriceIntel = aiPrice && askingPrice;
    // Same scale, wording and colours as the inventory table's AI Price chip.
    const gapDisplay = hasPriceIntel ? aiPriceGapDisplay(unit, gapThresholds) : null;
    const priceGapLabel = gapDisplay?.label;
    const priceGapColor = gapDisplay?.color;

    const predictedSaleDate = unit.PredictedSaleDate ? new Date(unit.PredictedSaleDate) : null;
    const countdown = saleCountdown(unit.PredictedSaleDate);

    // 12-month price trajectory — Today value from same ML pipeline + 12 forward months
    const trajectoryData = useMemo(() => {
        // Shared with the full unit page (utils/unitIntelligence).
        const t = buildTrajectory(predictions12m, unit);
        if (!t) return null;
        const {
            labels, prices, firstPrice, lastPrice, totalDrop, monthlyAvg, dropPct,
            hasBand, bandLow, bandHigh, bandLowPct, bandHighPct,
        } = t;

        return {
            labels,
            allPrices: prices,
            firstPrice,
            lastPrice,
            totalDrop,
            monthlyAvg,
            dropPct,
            hasBand,
            bandLowPct,
            bandHighPct,
            chartData: {
                labels,
                datasets: [{
                    label: 'Predicted Value',
                    data: prices,
                    borderColor: '#6f8f5c',
                    backgroundColor: 'rgba(111, 143, 92, 0.14)',
                    borderWidth: 2.4,
                    fill: true,
                    tension: 0.35,
                    pointRadius: 4,
                    pointHoverRadius: 7,
                    pointBackgroundColor: '#6f8f5c',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    order: 0,
                }, ...(hasBand ? [{
                    label: 'Upper bound',
                    data: bandHigh,
                    borderColor: 'rgba(111, 143, 92, 0.30)',
                    backgroundColor: 'rgba(111, 143, 92, 0.10)',
                    borderWidth: 1,
                    borderDash: [4, 4],
                    fill: '+1',
                    tension: 0.35,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    order: 2,
                }, {
                    label: 'Lower bound',
                    data: bandLow,
                    borderColor: 'rgba(111, 143, 92, 0.30)',
                    backgroundColor: 'transparent',
                    borderWidth: 1,
                    borderDash: [4, 4],
                    fill: false,
                    tension: 0.35,
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    order: 3,
                }] : [])],
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        padding: 10,
                        titleColor: '#fff',
                        bodyColor: '#fff',
                        borderColor: '#6f8f5c',
                        borderWidth: 1,
                        displayColors: false,
                        callbacks: {
                            // One tooltip row per series would show the same month three
                            // times; label the bounds instead of repeating the value.
                            label: (ctx) => {
                                const n = ctx.dataset.label;
                                const v = formatCurrency(ctx.parsed.y);
                                if (n === 'Upper bound') return `Optimistic ${v}`;
                                if (n === 'Lower bound') return `Conservative ${v}`;
                                return v;
                            },
                        },
                    },
                },
                scales: {
                    y: {
                        beginAtZero: false,
                        ticks: {
                            callback: (v) => '$' + (v / 1000).toFixed(0) + 'k',
                            color: '#666',
                            font: { size: 11 },
                        },
                        grid: { color: '#e0e0e0', drawBorder: false },
                    },
                    x: {
                        ticks: { color: '#666', font: { size: 11 } },
                        grid: { display: false },
                    },
                },
            },
        };
    }, [predictions12m, unit.PredictedPrice, unit.MLPrediction]); // eslint-disable-line react-hooks/exhaustive-deps

    // Days-to-sell survival forecast — chance sold over the next 12 months
    const forecastChart = useMemo(() => {
        const points = survivalPoints(unit);
        if (!points) return null;
        const { labels, soldPct } = points;
        return {
            chartData: {
                labels,
                datasets: [{
                    label: 'Chance sold',
                    data: soldPct,
                    borderColor: '#689F38',
                    backgroundColor: 'rgba(139, 195, 74, 0.12)',
                    borderWidth: 2.5,
                    fill: true,
                    tension: 0.35,
                    pointRadius: 3,
                    pointHoverRadius: 6,
                    pointBackgroundColor: '#689F38',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    order: 0,
                }],
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: 'rgba(0,0,0,0.8)', padding: 10,
                        titleColor: '#fff', bodyColor: '#fff',
                        borderColor: '#689F38', borderWidth: 1, displayColors: false,
                        callbacks: { label: (ctx) => `${ctx.parsed.y}% sold by ${ctx.label}` },
                    },
                },
                scales: {
                    y: {
                        min: 0, max: 100,
                        ticks: { callback: (v) => v + '%', color: '#666', font: { size: 11 } },
                        grid: { color: '#e0e0e0', drawBorder: false },
                    },
                    x: { ticks: { color: '#666', font: { size: 11 } }, grid: { display: false } },
                },
            },
        };
    }, [unit.SurvivalCurve]); // eslint-disable-line react-hooks/exhaustive-deps

    // Surface whatever signals exist — don't blank the whole tab just because the
    // price/sell-speed model didn't score this unit. Each section self-gates below;
    // this only decides "tab body" vs. the "no insights" fallback.
    const hasQuoteInterest = computeHasQuoteInterest(unit);
    const hasAnyInsight = computeHasAnyInsight(unit, { mfData, comps, trajectory: trajectoryData });

    return (
        <div className="tab-content">

            {/* Anomaly Signal */}
            {unit.AnomalyVerdict && unit.AnomalySeverity !== 'normal' && (() => {
                const cfg = {
                    critical: { bg: '#fff5f5', border: '#fca5a5', icon: '#b91c1c', label: '#b91c1c' },
                    warning:  { bg: '#fffbeb', border: '#fcd34d', icon: '#b45309', label: '#b45309' },
                    positive: { bg: '#f0fdf4', border: '#86efac', icon: '#15803d', label: '#15803d' },
                }[unit.AnomalySeverity] || null;
                if (!cfg) return null;
                return (
                    <section className="detail-section">
                        <div className="anomaly-detail-card" style={{ background: cfg.bg, borderColor: cfg.border }}>
                            <div className="anomaly-detail-header">
                                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" style={{ color: cfg.icon, flexShrink: 0 }}>
                                    {unit.AnomalySeverity === 'positive'
                                        ? <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        : <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    }
                                </svg>
                                <span className="anomaly-detail-verdict" style={{ color: cfg.label }}>{unit.AnomalyVerdict}</span>
                            </div>
                            {anomalyReasonText(unit) && <p className="anomaly-detail-reason">{anomalyReasonText(unit)}</p>}
                            <div className="anomaly-detail-context">
                                {anomalyContext(unit).map((text) => (
                                    <span key={text} className="anomaly-ctx-item">{text}</span>
                                ))}
                            </div>
                        </div>
                    </section>
                );
            })()}

            {hasAnyInsight ? (
                <>
                    {/* Sell-Speed Risk (calibrated Ladenhüter risk) */}
                    {unit.RiskTier && (
                        <section className="detail-section is-card">
                            <div className="is-card-head">
                                <span className="is-card-eyebrow"><IronSightMark /> IronSight</span>
                                <span className="is-card-title">Sell-Speed Risk</span>
                            </div>
                            <div className="is-card-body">
                            <div className="risk-card" style={{
                                borderColor: riskStyle.border,
                                background: `linear-gradient(135deg, ${riskStyle.bg} 0%, white 100%)`,
                            }}>
                                <div className="risk-card-head">
                                    <div className="risk-score">
                                        <span className="risk-score-value" style={{ color: riskStyle.text }}>
                                            {unit.LadenhueterRisk != null ? `${unit.LadenhueterRisk.toFixed(0)}%` : '—'}
                                        </span>
                                        <span className="risk-score-label">slow-mover risk</span>
                                    </div>
                                    <span className="risk-tier-badge" style={{
                                        color: riskStyle.text,
                                        backgroundColor: riskStyle.bg,
                                        borderColor: riskStyle.border,
                                    }}>
                                        <span className="risk-tier-dot" style={{ backgroundColor: riskStyle.dot }} />
                                        {riskStyle.label} Risk
                                    </span>
                                </div>
                                <p className="risk-card-note is-rec">
                                    <span className="is-rec-lead">Recommendation:</span>
                                    {riskRecommendation(unit.RiskTier)}
                                </p>
                            </div>
                            </div>
                        </section>
                    )}

                    {/* Days-to-Sell Forecast (survival model) */}
                    {unit.MedianDays != null && (
                        <section className="detail-section">
                            <div className="section-heading-row">
                                <h3>Days-to-Sell Forecast</h3>
                                <span className="info-tooltip">
                                    <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15" className="info-icon">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <span className="tooltip-text">
                                        <strong>How long until this unit sells.</strong><br />
                                        An AI survival model, trained on years of past sales, estimates the
                                        chance of selling by each future month — that's the green curve
                                        (0% today, rising toward 100%).<br /><br />
                                        <strong>Typical range ({unit.RangeLowDays}–{unit.RangeHighDays}d)</strong> — the middle half of likely outcomes (25th–75th percentile).<br />
                                        <strong>Median (~{unit.MedianDays}d)</strong> — where the curve crosses 50%: half of similar units sell faster, half slower.<br />
                                        <strong>Up to ~{unit.UpperDays}d if slow</strong> — a calibrated slow-case bound: about 9 in 10 sell by then, the rest take longer.<br /><br />
                                        <strong>The % tiles</strong> read the curve at 30 / 60 / 90 / 180 days — e.g. "{unit.PSoldBy90 != null ? `${unit.PSoldBy90.toFixed(0)}% sold by 90d` : 'sold by 90d'}".<br />
                                        <strong>Est. carrying to sale</strong> = median days × this unit's daily floor-plan cost.<br /><br />
                                        Percentages are calibrated on held-out sales, so they reflect what
                                        actually happened, not just model confidence. Sell-time is skewed —
                                        most go quickly, a few drag on — so we show a range, never one exact day.
                                    </span>
                                </span>
                            </div>
                            <div className="forecast-card">
                                <div className="forecast-headline">
                                    <span className="forecast-range">
                                        {unit.RangeLowDays}–{unit.RangeHighDays}<span className="forecast-unit"> days</span>
                                    </span>
                                    <span className="forecast-sub">
                                        typical · median ~{unit.MedianDays}d · up to ~{unit.UpperDays}d if slow
                                    </span>
                                    {unit.ExpectedSaleDate && (
                                        <span className="forecast-date">Expected sale ≈ {formatDate(unit.ExpectedSaleDate)}</span>
                                    )}
                                </div>
                                {forecastChart && (
                                    <div className="forecast-chart-wrapper">
                                        <Line data={forecastChart.chartData} options={forecastChart.chartOptions} />
                                    </div>
                                )}
                                <div className="forecast-prob-grid">
                                    {[['30d', unit.PSoldBy30], ['60d', unit.PSoldBy60], ['90d', unit.PSoldBy90], ['180d', unit.PSoldBy180]].map(([lbl, val]) => (
                                        <div className="forecast-prob" key={lbl}>
                                            <span className="forecast-prob-val">{val != null ? `${val.toFixed(0)}%` : '—'}</span>
                                            <span className="forecast-prob-lbl">sold by {lbl}</span>
                                        </div>
                                    ))}
                                </div>
                                {unit.CarryingCostDaily != null && unit.StockStatus !== 'P' && (
                                    <div className="forecast-carrying">
                                        Est. carrying to sale <strong>{formatCurrency(unit.MedianDays * unit.CarryingCostDaily)}</strong>
                                        <span className="forecast-carrying-sub"> ({unit.MedianDays}d × {formatCurrency(unit.CarryingCostDaily)}/day)</span>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Buyer Interest */}
                    {(hasQuoteInterest || hasSellSpeedData) && (
                    <section className="detail-section is-card">
                        <div className="is-card-head">
                            <span className="is-card-eyebrow"><IronSightMark /> IronSight</span>
                            <span className="is-card-title">Buyer Interest</span>
                        </div>
                        <div className="is-card-body">
                        <div className="buyer-interest-card">
                            <div className="bi-header">
                                <span className="bi-heat-badge" style={{ color: heatStatus.color, backgroundColor: heatStatus.bg }}>
                                    <span className="bi-heat-dot" style={{ backgroundColor: heatStatus.color }} />
                                    {heatStatus.label}
                                </span>
                                <div className="bi-stats">
                                    <span className="bi-stat"><strong>{unit.QuoteCount || 0}</strong> quote{unit.QuoteCount !== 1 ? 's' : ''}</span>
                                    <span className="bi-sep">·</span>
                                    <span className="bi-stat">
                                        {unit.DaysSinceLastQuote != null ? `Last quoted ${unit.DaysSinceLastQuote}d ago` : 'Never quoted'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        </div>
                    </section>
                    )}

                    {/* Price Intelligence */}
                    {hasPriceIntel && (
                        <section className="detail-section is-card">
                            <div className="is-card-head">
                                <span className="is-card-eyebrow"><IronSightMark /> IronSight</span>
                                <span className="is-card-title">Price Intelligence</span>
                            </div>
                            <div className="is-card-body">
                            <div className="price-intel-card">
                                <div className="price-compare-row">
                                    <div className="price-compare-item">
                                        <span className="price-compare-label">Asking Price</span>
                                        <span className="price-compare-value">{formatCurrency(askingPrice)}</span>
                                    </div>
                                    <div className="price-compare-vs">vs</div>
                                    <div className="price-compare-item">
                                        <span className="price-compare-label">IronSight Estimate</span>
                                        <span className="price-compare-value ai-price">{formatCurrency(aiPrice)}</span>
                                    </div>
                                </div>
                                <div className="price-gap-bar">
                                    <div className="price-bar-track">
                                        <div className="price-bar-fill" style={{
                                            width: `${Math.min(100, Math.max(5, (Math.min(askingPrice, aiPrice) / Math.max(askingPrice, aiPrice)) * 100))}%`,
                                            backgroundColor: priceGapColor
                                        }} />
                                        <div className="price-bar-marker" style={{ left: '100%' }} />
                                    </div>
                                    <div className="price-gap-label" style={{ color: priceGapColor }}>
                                        <span className="price-gap-pct">{gapDisplay?.pctText}</span>
                                        <span className="price-gap-text">{priceGapLabel}</span>
                                    </div>
                                </div>
                                {predictedSaleDate && (
                                    <div className="predicted-sale-row">
                                        <div className="predicted-sale-info">
                                            <span className="predicted-sale-label">Prediction made on</span>
                                            <span className="predicted-sale-date">{formatDate(unit.PredictedSaleDate)}</span>
                                        </div>
                                        {countdown && (
                                            <div className={`sale-countdown ${countdown.tone}`}>
                                                {countdown.text}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            </div>
                        </section>
                    )}

                    {/* 12-Month Price Trajectory */}
                    {predictions12mLoading && (
                        <section className="detail-section">
                            <div className="hist-sell-card" style={{ textAlign: 'center', padding: 'var(--spacing-lg)', color: 'var(--text-muted)' }}>
                                <span className="hist-sell-label">Loading price trajectory...</span>
                            </div>
                        </section>
                    )}
                    {!predictions12mLoading && trajectoryData && (
                        <section className="detail-section is-card">
                            <div className="is-card-head">
                                <span className="is-card-eyebrow"><IronSightMark /> IronSight</span>
                                <span className="is-card-title">12-Month Price Trajectory</span>
                            </div>
                            <div className="is-card-body">
                            <div className="price-trajectory-card">
                                <p className="is-chart-caption">
                                    {trajectoryData.hasBand
                                        ? `Predicted resale value \u00b7 shaded band \u2212${trajectoryData.bandLowPct}% / +${trajectoryData.bandHighPct}% at month 12`
                                        : 'Predicted resale value'}
                                </p>
                                <div className="trajectory-chart-wrapper">
                                    <Line data={trajectoryData.chartData} options={trajectoryData.chartOptions} />
                                </div>
                                <div className="is-rows">
                                    <div className="is-row">
                                        <span className="is-row-label">Today</span>
                                        <span className="is-row-value">{formatCurrency(trajectoryData.firstPrice)}</span>
                                    </div>
                                    <div className="is-row">
                                        <span className="is-row-label">Month 12</span>
                                        <span className="is-row-value">{formatCurrency(trajectoryData.lastPrice)}</span>
                                    </div>
                                    <div className="is-row">
                                        <span className="is-row-label">
                                            {trajectoryData.totalDrop > 0 ? 'Total Depreciation' : 'Total Appreciation'}
                                        </span>
                                        <span className="is-row-value" style={{
                                            color: trajectoryData.totalDrop > 0 ? '#b91c1c' : '#15803d'
                                        }}>
                                            {trajectoryData.totalDrop > 0 ? '-' : '+'}{formatCurrency(Math.abs(trajectoryData.totalDrop))}
                                            <span className="trajectory-stat-pct">
                                                ({trajectoryData.totalDrop > 0 ? '-' : '+'}{Math.abs(trajectoryData.dropPct).toFixed(1)}%)
                                            </span>
                                        </span>
                                    </div>
                                    <div className="is-row">
                                        <span className="is-row-label">
                                            {trajectoryData.monthlyAvg > 0 ? 'Avg Monthly Loss' : 'Avg Monthly Gain'}
                                        </span>
                                        <span className="is-row-value" style={{
                                            color: trajectoryData.monthlyAvg > 0 ? '#b91c1c' : '#15803d'
                                        }}>
                                            {trajectoryData.monthlyAvg > 0 ? '-' : '+'}{formatCurrency(Math.abs(trajectoryData.monthlyAvg))}/mo
                                        </span>
                                    </div>
                                </div>
                            </div>
                            </div>
                        </section>
                    )}

                    {/* Peer Comparisons — loading state */}
                    {!comps && (
                        <section className="detail-section">
                            <div className="hist-sell-card" style={{ textAlign: 'center', padding: 'var(--spacing-lg)', color: 'var(--text-muted)' }}>
                                <span className="hist-sell-label">Loading peer comparisons...</span>
                            </div>
                        </section>
                    )}

                    {/* Listing Duration vs Peers (from comps active peers) */}
                    {mfListing && unit.MFDaysListed != null && (() => {
                        const cmp = peerComparison(unit.MFDaysListed, mfListing, 'listing');
                        const { pct } = cmp;
                        const color = PEER_BAND_COLORS[cmp.band];
                        return (
                            <section className="detail-section is-card">
                                <div className="is-card-body">
                                <h3 className="is-section-title">Listing Duration vs Peers</h3>
                                <div className="hist-sell-card">
                                    <div className="hist-sell-cols">
                                        <div className="hist-sell-col">
                                            <span className="hist-sell-label">THIS UNIT</span>
                                            <span className="hist-sell-value">{unit.MFDaysListed}d</span>
                                        </div>
                                        <div className="hist-sell-col hist-sell-col-right">
                                            <span className="hist-sell-label">PEER AVG</span>
                                            <span className="hist-sell-value">{Math.round(mfListing.avg)}d</span>
                                            <span className="hist-sell-count">({mfListing.count} listed)</span>
                                        </div>
                                    </div>
                                    <div className="hist-sell-range">
                                        {Math.round(mfListing.avg)}d avg · {Math.round(mfListing.min)}d shortest · {Math.round(mfListing.max)}d longest
                                    </div>
                                    <div className="hist-sell-bar-row">
                                        <div className="hist-sell-bar-track">
                                            <div className="hist-sell-bar-fill" style={{
                                                width: `${cmp.barWidth}%`,
                                                backgroundColor: color
                                            }} />
                                        </div>
                                        <span className="hist-sell-bar-pct" style={{ color }}>{Math.round(pct)}% of avg</span>
                                    </div>
                                    <p className="hist-sell-verdict" style={{ color }}>{cmp.verdict}</p>
                                    {onSwitchTab && (
                                        <button className="hist-sell-link" onClick={() => onSwitchTab('comps')}>
                                            View active peers →
                                        </button>
                                    )}
                                </div>
                                </div>
                            </section>
                        );
                    })()}

                    {/* Operation Hours vs Peers (from comps active peers) */}
                    {hoursStats && unit.ODOMETER != null && (() => {
                        const cmp = peerComparison(unit.ODOMETER, hoursStats, 'hours');
                        const { pct } = cmp;
                        const color = PEER_BAND_COLORS[cmp.band];
                        return (
                            <section className="detail-section is-card">
                                <div className="is-card-body">
                                <h3 className="is-section-title">Operation Hours vs Peers</h3>
                                <div className="hist-sell-card">
                                    <div className="hist-sell-cols">
                                        <div className="hist-sell-col">
                                            <span className="hist-sell-label">THIS UNIT</span>
                                            <span className="hist-sell-value">{formatNumber(unit.ODOMETER)}h</span>
                                        </div>
                                        <div className="hist-sell-col hist-sell-col-right">
                                            <span className="hist-sell-label">PEER AVG</span>
                                            <span className="hist-sell-value">{formatNumber(Math.round(hoursStats.avg))}h</span>
                                            <span className="hist-sell-count">({hoursStats.count} units)</span>
                                        </div>
                                    </div>
                                    <div className="hist-sell-range">
                                        {formatNumber(Math.round(hoursStats.avg))}h avg · {formatNumber(Math.round(hoursStats.min))}h lowest · {formatNumber(Math.round(hoursStats.max))}h highest
                                    </div>
                                    <div className="hist-sell-bar-row">
                                        <div className="hist-sell-bar-track">
                                            <div className="hist-sell-bar-fill" style={{
                                                width: `${cmp.barWidth}%`,
                                                backgroundColor: color
                                            }} />
                                        </div>
                                        <span className="hist-sell-bar-pct" style={{ color }}>{Math.round(pct)}% of avg</span>
                                    </div>
                                    <p className="hist-sell-verdict" style={{ color }}>{cmp.verdict}</p>
                                    {onSwitchTab && (
                                        <button className="hist-sell-link" onClick={() => onSwitchTab('comps')}>
                                            View active peers →
                                        </button>
                                    )}
                                </div>
                                </div>
                            </section>
                        );
                    })()}

                    {/* Carrying Cost + Margin Calculator */}
                    <CarryingCostSection unit={unit} calcPrice={calcPrice} setCalcPrice={setCalcPrice} pipeline={pipeline} quoteEquipment={quoteEquipment} />

                    {/* MachineFinder Intel */}
                    {mfData && (
                        <section className="detail-section is-card">
                            <div className="is-card-body">
                            <h3 className="is-section-title">{SOURCE_LABELS.listingInsights}</h3>
                            <div className="mf-intel-grid">
                                {mfData.advertised_price != null && (
                                    <div className="mf-intel-card">
                                        <span className="mf-intel-label">Advertised Price</span>
                                        <span className="mf-intel-value">{formatCurrency(mfData.advertised_price)}</span>
                                        {unit.Best_Sell_Price && (() => {
                                            const adv = advertisedVsInternal(mfData.advertised_price, unit.Best_Sell_Price);
                                            return (
                                                <span className="mf-intel-sub" style={{ color: adv.tone === 'pos' ? '#15803d' : '#b91c1c' }}>
                                                    {adv.text}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                )}
                                {mfData.days_listed != null && (
                                    <div className="mf-intel-card">
                                        <span className="mf-intel-label">Listed for</span>
                                        <span className="mf-intel-value" style={{ color: getListingAgeColor(mfData.days_listed) }}>
                                            {mfData.days_listed} days
                                        </span>
                                        {mfData.days_since_modified != null && (
                                            <span className="mf-intel-sub">Last updated {mfData.days_since_modified}d ago</span>
                                        )}
                                    </div>
                                )}
                                <div className="mf-intel-card">
                                    <span className="mf-intel-label">Listing Quality</span>
                                    <span className="mf-intel-value" style={{ color: getCompletenessColor(mfData.completeness_pct) }}>
                                        {mfData.completeness_score}/6
                                    </span>
                                    {mfData.missing_fields.length > 0 && (
                                        <span className="mf-intel-sub">Missing: {mfData.missing_fields.join(', ')}</span>
                                    )}
                                </div>
                            </div>
                            </div>
                        </section>
                    )}
                    {mfLoading && !mfData && (
                        <section className="detail-section">
                            <div className="mf-loading">Loading {SOURCE_LABELS.listingLower} data...</div>
                        </section>
                    )}
                </>
            ) : (
                // Only reached when the unit has genuinely no signal to show:
                // no price/sell-speed prediction, no quotes, no listing, no peer comps.
                <div className="empty-tab">
                    <p>No AI predictions available for this unit yet.</p>
                </div>
            )}
        </div>
    );
}

AITab.propTypes = {
    unit: PropTypes.object.isRequired,
    mfData: PropTypes.object,
    mfLoading: PropTypes.bool,
    calcPrice: PropTypes.number,
    setCalcPrice: PropTypes.func.isRequired,
};

AITab.defaultProps = {
    mfData: null,
    mfLoading: false,
    calcPrice: null,
};

export default AITab;
